import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, ChevronLeft, ChevronRight, GitCompare, RefreshCw, X } from "lucide-react";

const toDisplay = (value) => {
  if (value === undefined || value === null || value === "") {
    return "—";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return String(value);
};

const isTrueLike = (value) => value === true || value === "true" || value === 1 || value === "1";
const hasContent = (value) => value !== undefined && value !== null && String(value).trim() !== "";
const isRenderableObject = (value) => typeof value === "object" && value !== null;
const formatSummaryLabel = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
const SUMMARY_PLACEHOLDER = "--";
const SUMMARY_SEPARATOR = " | ";

const hasSummaryContent = (summary) => {
  if (!isRenderableObject(summary)) {
    return hasContent(summary);
  }

  if (Array.isArray(summary)) {
    return summary.some((item) => hasSummaryContent(item));
  }

  return Object.values(summary).some((value) => hasSummaryContent(value));
};

const getSummaryPreview = (summary) => {
  if (!isRenderableObject(summary)) {
    return toDisplay(summary);
  }

  if (Array.isArray(summary)) {
    const previewParts = summary
      .map((item) => getSummaryPreview(item))
      .filter((item) => item && item !== SUMMARY_PLACEHOLDER);
    return previewParts.length ? previewParts.join(SUMMARY_SEPARATOR) : SUMMARY_PLACEHOLDER;
  }

  const previewParts = Object.entries(summary)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        const firstValue = value.find((item) => hasContent(item));
        return firstValue ? `${formatSummaryLabel(key)}: ${firstValue}` : "";
      }

      if (isRenderableObject(value)) {
        return `${formatSummaryLabel(key)}: ${getSummaryPreview(value)}`;
      }

      return hasContent(value) ? `${formatSummaryLabel(key)}: ${value}` : "";
    })
    .filter(Boolean);

  return previewParts.length ? previewParts.join(SUMMARY_SEPARATOR) : SUMMARY_PLACEHOLDER;
};
const SUCCESS_STATUSES = new Set(["SENT", "DELIVERED", "READ", "ACCEPTED", "SUCCESS"]);

const hasSuccessfulNotification = (logs = []) =>
  Array.isArray(logs) &&
  logs.some((log) =>
    [log?.status, log?.message_status, log?.provider_response?.messages?.[0]?.message_status]
      .map((value) => String(value || "").trim().toUpperCase())
      .some((status) => SUCCESS_STATUSES.has(status)),
  );

const Badge = ({ value, tone = "slate" }) => {
  const toneMap = {
    emerald: "border-emerald-500/20 bg-emerald-500/10 text-emerald-300",
    blue: "border-sky-500/20 bg-sky-500/10 text-sky-300",
    amber: "border-amber-500/20 bg-amber-500/10 text-amber-300",
    slate: "border-slate-700 bg-slate-800/80 text-slate-200",
  };

  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${toneMap[tone] || toneMap.slate}`}>
      {value}
    </span>
  );
};

const TableSkeleton = () => (
  <div className="space-y-3 p-5">
    {Array.from({ length: 6 }).map((_, index) => (
      <div key={index} className="h-12 animate-pulse rounded-2xl bg-slate-900/90" />
    ))}
  </div>
);

const NotificationBadges = ({ notifications }) => {
  const sentToUser = notifications?.sent_to_user;
  const sentToCandidate = notifications?.sent_to_candidate;

  const badges = [
    hasSuccessfulNotification(sentToUser?.push_logs) ? { label: "User Push Sent", tone: "blue" } : null,
    hasSuccessfulNotification(sentToUser?.whatsapp_templates) ? { label: "User Template Sent", tone: "emerald" } : null,
    hasSuccessfulNotification(sentToCandidate?.push_logs) ? { label: "Candidate Push Sent", tone: "blue" } : null,
    hasSuccessfulNotification(sentToCandidate?.whatsapp_templates) ? { label: "Candidate Template Sent", tone: "emerald" } : null,
  ].filter(Boolean);

  if (badges.length === 0) {
    return <span className="text-xs text-slate-500">No channel logs</span>;
  }

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {badges.map((badge) => (
        <Badge key={badge.label} value={badge.label} tone={badge.tone} />
      ))}
    </div>
  );
};

const SummaryValue = ({ value }) => {
  if (!isRenderableObject(value)) {
    return <p className="leading-7 text-slate-200">{toDisplay(value)}</p>;
  }

  if (Array.isArray(value)) {
    const items = value.filter((item) => hasContent(item) || isRenderableObject(item));

    if (items.length === 0) {
      return <p className="leading-7 text-slate-400">{SUMMARY_PLACEHOLDER}</p>;
    }

    return (
      <ul className="space-y-2 text-slate-200">
        {items.map((item, index) => (
          <li key={index} className="rounded-2xl border border-slate-800 bg-slate-900/60 px-4 py-3">
            <SummaryValue value={item} />
          </li>
        ))}
      </ul>
    );
  }

  const entries = Object.entries(value).filter(([, nestedValue]) => hasContent(nestedValue) || isRenderableObject(nestedValue));

  if (entries.length === 0) {
    return <p className="leading-7 text-slate-400">{SUMMARY_PLACEHOLDER}</p>;
  }

  return (
    <div className="space-y-4">
      {entries.map(([key, nestedValue]) => (
        <div key={key} className="rounded-2xl border border-slate-800 bg-slate-900/60 px-4 py-4">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">{formatSummaryLabel(key)}</p>
          <div className="mt-3">
            <SummaryValue value={nestedValue} />
          </div>
        </div>
      ))}
    </div>
  );
};

const EmptyState = ({ title, description, action }) => (
  <div className="flex min-h-[240px] flex-col items-center justify-center gap-4 p-8 text-center">
    <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-950/60 p-4 text-slate-300">
      <AlertCircle size={18} />
    </div>
    <div>
      <p className="text-base font-semibold text-white">{title}</p>
      <p className="mt-2 max-w-xl text-sm leading-6 text-slate-400">{description}</p>
    </div>
    {action}
  </div>
);

const PersonCell = ({ person, analyticsLink }) => {
  const personId = person?.user_id || person?.id || person?.candidate_id || "";
  const personName = person?.name || person?.username || "Unknown";

  return (
    <div className="min-w-[220px]">
      <p className="font-medium text-white">{personName}</p>
      <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-400">
        <span>{toDisplay(personId)}</span>
        {person?.phone_number || person?.phone ? <span>{toDisplay(person?.phone_number || person?.phone)}</span> : null}
        {person?.assigned_to ? <span>{toDisplay(person?.assigned_to)}</span> : null}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {personId ? (
          <Link
            to={`/users/user/${personId}`}
            className="inline-flex items-center rounded-full border border-sky-500/20 bg-sky-500/10 px-2.5 py-1 text-[11px] font-semibold text-sky-300 transition hover:bg-sky-500/20"
          >
            Profile
          </Link>
        ) : null}
        {analyticsLink ? (
          <Link
            to={analyticsLink}
            className="inline-flex items-center rounded-full border border-slate-700 bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-slate-200 transition hover:border-slate-500 hover:text-white"
          >
            Analytics
          </Link>
        ) : null}
        {person?.is_premium ? (
          <div
          title="Premium"
          // className="inline-flex items-center rounded-full border border-indigo-500 bg-indigo-500/10 px-2.5 py-1 text-[11px] font-semibold text-indigo-400 transition hover:bg-indigo-600 hover:text-white"
            className="inline-flex items-center rounded-full border border-amber-400 bg-slate-950 px-2.5 py-1 text-[11px] font-black text-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.2)]"
            // className="inline-flex items-center rounded-full border border-amber-500/50 bg-amber-500/10 px-2.5 py-1 text-[11px] font-bold text-amber-500 transition hover:bg-amber-500 hover:text-white"
          >
            $
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default function MatchesFeedTable({
  rows = [],
  pagination,
  loading,
  error,
  onRetry,
  onPageChange,
  onLimitChange,
  formatDateTime,
  currentSearch = "",
  highlightedRowId = "",
  highlightedRowNumber = "",
}) {
  const [selectedSummary, setSelectedSummary] = useState(null);
  const limit = Number(pagination?.limit || 20);
  const offset = Number(pagination?.offset || 0);
  const total = Number(pagination?.total || 0);
  const hasMore = Boolean(pagination?.has_more);
  const showingFrom = total > 0 ? offset + 1 : 0;
  const showingTo = total > 0 ? Math.min(offset + rows.length, total) : rows.length;
  const currentPage = Math.floor(offset / Math.max(limit, 1)) + 1;
  const totalPages = total > 0 ? Math.max(1, Math.ceil(total / Math.max(limit, 1))) : currentPage;

  useEffect(() => {
    if (!highlightedRowId && !highlightedRowNumber) {
      return;
    }

    const highlightedRow = document.querySelector('[data-feed-highlight="true"]');
    highlightedRow?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightedRowId, highlightedRowNumber, rows, offset]);

  return (
    <section className="rounded-3xl border border-slate-800 bg-slate-950/80 shadow-[0_24px_80px_rgba(15,23,42,0.28)]">
      <div className="flex flex-col gap-4 border-b border-slate-800 px-5 py-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white">Admin Match Feed</h2>
          <p className="mt-1 text-sm text-slate-400">
            Cross-user published matchmaking feed using admin visibility rules.
          </p>
          {highlightedRowId || highlightedRowNumber ? (
            <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-sky-500/20 bg-sky-500/10 px-3 py-1.5 text-xs font-semibold text-sky-300">
              <span>Last opened row</span>
              <span>{highlightedRowNumber ? `#${highlightedRowNumber}` : String(highlightedRowId)}</span>
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
            {total > 0 ? `Showing ${showingFrom}-${showingTo} of ${total}` : `${rows.length} rows`}
          </div>
          <select
            value={String(limit)}
            onChange={(event) => onLimitChange(Number(event.target.value))}
            className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 outline-none transition focus:border-sky-400"
          >
            {[10, 20, 50, 100].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize} / page
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm font-medium text-slate-200 transition hover:border-sky-500 hover:text-white"
          >
            <RefreshCw size={14} />
            Retry
          </button>
        </div>
      </div>

      {loading ? (
        <TableSkeleton />
      ) : error ? (
        <EmptyState
          title="Unable to load the admin feed"
          description={error.message || "The admin feed API returned an error for the current filters."}
          action={
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center gap-2 rounded-xl bg-sky-500 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-sky-400"
            >
              <RefreshCw size={14} />
              Try again
            </button>
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No matches found in the admin feed"
          description="Try clearing the UID filter, widening the published date range, or disabling the Today-only toggle."
        />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-[1760px] text-left">
              <thead className="bg-slate-900/80">
                <tr>
                  {[
                    "User",
                    "Candidate",
                    "Match Score",
                    "Gunn Score",
                    "Proximity",
                    "Compatibility",
                    "Source Service",
                    "Published At",
                    "Created At",
                    "Viewed",
                    "Notified",
                    "Summary",
                  ].map((label) => (
                    <th
                      key={label}
                      className="whitespace-nowrap border-b border-slate-800 px-4 py-3 text-xs font-semibold uppercase tracking-[0.24em] text-slate-500"
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const rowKey = row.id || `${row.user?.user_id || "user"}-${row.candidate?.user_id || "candidate"}-${index}`;
                  const rowNumber = offset + index + 1;
                  const rowIdentifier = row.id || row.match_id || rowKey;
                  const isHighlightedRow =
                    (highlightedRowId && String(highlightedRowId) === String(rowIdentifier)) ||
                    (highlightedRowNumber && Number(highlightedRowNumber) === rowNumber);
                  const analyticsLink = row.user?.user_id
                    ? {
                        pathname: `/matches/${row.user.user_id}`,
                        search: `?${new URLSearchParams({
                          from_feed: "true",
                          feed_return: currentSearch,
                          feed_row: String(rowNumber),
                          feed_row_id: String(rowIdentifier),
                          feed_user: row.user?.name || row.user?.username || row.user?.user_id || "",
                          feed_candidate: row.candidate?.name || row.candidate?.username || row.candidate?.user_id || "",
                        }).toString()}`,
                      }
                    : "";

                  return (
                    <tr
                      key={rowKey}
                      data-feed-highlight={isHighlightedRow ? "true" : "false"}
                      className={`border-b border-slate-900/80 text-sm text-slate-300 transition hover:bg-slate-900/50 ${isHighlightedRow ? "bg-sky-500/10" : ""}`}
                    >
                      <td className="px-4 py-3 align-top">
                        <PersonCell person={row.user} analyticsLink={analyticsLink} />
                        {isHighlightedRow ? (
                          <div className="mt-2">
                            <Badge value={`Clicked Row #${rowNumber}`} tone="blue" />
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <PersonCell person={row.candidate} />
                        {row.user?.user_id && row.candidate?.user_id ? (
                          <Link
                            to={`/match-pair?user_id=${encodeURIComponent(row.user.user_id)}&candidate_id=${encodeURIComponent(row.candidate.user_id)}`}
                            className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-indigo-500/20 bg-indigo-500/10 px-2.5 py-1 text-[11px] font-semibold text-indigo-300 transition hover:bg-indigo-500/20"
                          >
                            <GitCompare size={12} />
                            Compare
                          </Link>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">{toDisplay(row.match_score)}</td>
                      <td className="px-4 py-3">{toDisplay(row.gunn_score)}</td>
                      <td className="px-4 py-3" title={`${toDisplay(row.proximity)} `}>{toDisplay(row.proximity)}</td>
                      <td className="px-4 py-3">
                        <Badge value={toDisplay(row.compatibility_level)} tone="blue" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1.5 items-start">
                          <Badge
                            value={toDisplay(
                              !row.source_service || row.source_service === "unknown"
                                ? "default_service"
                                : row.source_service
                            )}
                            tone="emerald"
                          />
                          {row.created_by && (
                            <div className="flex items-center gap-1 bg-slate-900 px-2 py-0.5 rounded border border-slate-800 text-[10px]">
                              <span className="text-slate-500 font-bold uppercase text-[7px] tracking-tighter">Created By:</span>
                              <span className="font-semibold text-slate-300">{row.created_by}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">{formatDateTime(row.published_at)}</td>
                      <td className="whitespace-nowrap px-4 py-3">{formatDateTime(row.created_at)}</td>
                      <td className="px-4 py-3">
                        <Badge value={toDisplay(row.is_viewed)} tone={isTrueLike(row.is_viewed) ? "emerald" : "slate"} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="min-w-[260px]">
                          <Badge value={toDisplay(row.is_notified)} tone={isTrueLike(row.is_notified) ? "emerald" : "amber"} />
                          <NotificationBadges notifications={row.notifications} />
                        </div>
                      </td>
                      <td className="max-w-[360px] px-4 py-3">
                        <div className="space-y-3">
                          <p className="line-clamp-3 leading-6 text-slate-300">{getSummaryPreview(row.summary)}</p>
                          {hasSummaryContent(row.summary) ? (
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedSummary({
                                  summary: row.summary,
                                  userName: row.user?.name || row.user?.username || row.user?.user_id || "User",
                                  candidateName:
                                    row.candidate?.name || row.candidate?.username || row.candidate?.user_id || "Candidate",
                                })
                              }
                              className="inline-flex items-center rounded-full border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs font-semibold text-slate-200 transition hover:border-sky-500 hover:text-white"
                            >
                              View Summary
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-4 border-t border-slate-800 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-400">
              Page <span className="font-semibold text-white">{currentPage}</span>
              {totalPages ? <> of <span className="font-semibold text-white">{totalPages}</span></> : null}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onPageChange(Math.max(0, offset - limit))}
                disabled={offset === 0}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 transition hover:border-sky-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft size={14} />
                Previous
              </button>
              <button
                type="button"
                onClick={() => onPageChange(offset + limit)}
                disabled={!hasMore}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 transition hover:border-sky-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
                <ChevronRight size={14} />
              </button>
            </div>
          </div>

          {selectedSummary ? (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 px-4">
              <div className="w-full max-w-2xl rounded-3xl border border-slate-800 bg-slate-950 shadow-[0_24px_80px_rgba(15,23,42,0.45)]">
                <div className="flex items-start justify-between gap-4 border-b border-slate-800 px-6 py-5">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Match Summary</p>
                    <h3 className="mt-2 text-lg font-semibold text-white">
                      {selectedSummary.userName} and {selectedSummary.candidateName}
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedSummary(null)}
                    className="inline-flex items-center justify-center rounded-full border border-slate-700 bg-slate-900 p-2 text-slate-300 transition hover:border-slate-500 hover:text-white"
                    aria-label="Close summary modal"
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
                  <SummaryValue value={selectedSummary.summary} />
                </div>
                <div className="flex justify-end border-t border-slate-800 px-6 py-4">
                  <button
                    type="button"
                    onClick={() => setSelectedSummary(null)}
                    className="inline-flex items-center rounded-xl bg-sky-500 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-sky-400"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
